import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import eCourseService from "../../services/elearning/eCourseService";
import ReactPlayer from "react-player";
// Use react-doc-viewer for inline document previews
// @ts-ignore - some versions of react-doc-viewer don't provide TypeScript types
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Download,
  BookmarkPlus,
  Clock,
  CheckCircle,
  Circle,
  FileText,
  Video,
  Image,
  Link as LinkIcon,
} from "lucide-react";

interface ModuleViewerProps {
  courseId?: string;
}

// Local type definitions for the component
interface Module {
  _id: string;
  title: string;
  description: string;
  estimatedDuration: number;
  order: number;
}

interface Content {
  _id: string;
  title: string;
  description?: string;
  type: "video" | "document" | "image" | "link" | "text";
  content?: string;
  mediaUrl?: string;
  externalUrl?: string;
  downloadUrl?: string;
  attachments?: any[];
}

interface ContentProgress {
  contentId: string;
  completed: boolean;
  progressPercentage: number;
}

interface ModuleProgress {
  completionPercentage: number;
  contentProgress?: ContentProgress[];
}

const ContentRenderer: React.FC<{
  content: Content;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}> = ({ content, onProgress, onComplete }) => {
  const [bookmarked, setBookmarked] = useState(false);

  const handleBookmark = async () => {
    try {
      // TODO: Replace with actual API call
      setBookmarked(!bookmarked);
    } catch (error) {
      console.error("Error toggling bookmark:", error);
    }
  };

  // Robust extractor that returns a clean YouTube embed URL when possible.
  // Handles forms like:
  // - https://youtu.be/VIDEO_ID
  // - https://www.youtube.com/watch?si=...&v=VIDEO_ID&feature=...
  // - https://www.youtube.com/embed/VIDEO_ID
  // - https://www.youtube.com/shorts/VIDEO_ID
  // - query-only strings like "v=VIDEO_ID&feature=youtu.be"
  const toYouTubeEmbedUrl = (videoUrl: string): string => {
    if (!videoUrl || typeof videoUrl !== "string") return "";
    const cleanUrl = videoUrl.trim();

    // If it's already an embed URL, normalize and return
    const embedMatch = cleanUrl.match(
      /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/
    );
    if (embedMatch && embedMatch[1])
      return `https://www.youtube.com/embed/${embedMatch[1]}`;

    // If it's a youtu.be link
    const shortMatch = cleanUrl.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (shortMatch && shortMatch[1])
      return `https://www.youtube.com/embed/${shortMatch[1]}`;

    // If it's a shorts link
    const shortsMatch = cleanUrl.match(
      /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/
    );
    if (shortsMatch && shortsMatch[1])
      return `https://www.youtube.com/embed/${shortsMatch[1]}`;

    // Try to extract v= param anywhere in the string (handles si= before v=)
    const vParamMatch =
      cleanUrl.match(/[?&]v=([A-Za-z0-9_-]{11})/i) ||
      cleanUrl.match(/\bv=([A-Za-z0-9_-]{11})\b/i);
    if (vParamMatch && vParamMatch[1])
      return `https://www.youtube.com/embed/${vParamMatch[1]}`;

    // If input is just a query string like "v=ID&..." or starts with 'si=' then contains v= later
    try {
      const queryOnly =
        cleanUrl.startsWith("?") || /^[A-Za-z0-9_=&-]+$/.test(cleanUrl);
      if (queryOnly) {
        const params = new URLSearchParams(cleanUrl.replace(/^\?+/, ""));
        const v = params.get("v");
        if (v && v.length === 11) return `https://www.youtube.com/embed/${v}`;
      }
    } catch (e) {
      // ignore
    }

    // Last resort: find any 11-char candidate (low confidence)
    const anyId = cleanUrl.match(/([A-Za-z0-9_-]{11})/);
    if (anyId && anyId[1]) return `https://www.youtube.com/embed/${anyId[1]}`;

    // Nothing found - return original so the caller can decide fallback
    return cleanUrl;
  };
  const getContentIcon = () => {
    switch (content.type) {
      case "video":
        return <Video className="w-5 h-5" />;
      case "document":
        return <FileText className="w-5 h-5" />;
      case "image":
        return <Image className="w-5 h-5" />;
      case "link":
        return <LinkIcon className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };
  {
    console.log("content.type is:", content.type);
  }
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Content Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-blue-600">{getContentIcon()}</div>
            <div>
              <h3 className="font-semibold text-gray-900">{content.title}</h3>
              <p className="text-sm text-gray-600 capitalize">
                {content.type} content
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleBookmark}
              className="p-2 text-gray-400 hover:text-yellow-500 transition-colors"
            >
              {bookmarked ? (
                <BookmarkPlus className="w-5 h-5 text-yellow-500" />
              ) : (
                <BookmarkPlus className="w-5 h-5" />
              )}
            </button>

            {content.downloadUrl && (
              <a
                href={content.downloadUrl}
                download
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <Download className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Content Body */}
      <div className="p-6">
        {content.description && (
          <p className="text-gray-600 mb-4">{content.description}</p>
        )}

        {/* Render based on content type */}
        {content.type === "video" &&
          (() => {
            console.log("Content object:", content);
            console.log("Content mediaUrl:", content.mediaUrl);
            console.log("Content externalUrl:", content.externalUrl);
            console.log("Content attachments:", content.attachments);

            const attachments = content.attachments || [];
            // Try to find a video URL from mediaUrl or attachments
            const videoAttachment = attachments.find(
              (att: any) =>
                att.fileUrl &&
                (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(att.fileUrl) ||
                  /\b(youtube\.com|youtu\.be)\b/i.test(att.fileUrl))
            );
            const videoUrl =
              content.mediaUrl ||
              content.externalUrl ||
              videoAttachment?.fileUrl ||
              "";

            console.log("Content data:", {
              mediaUrl: content.mediaUrl,
              externalUrl: content.externalUrl,
              videoAttachment: videoAttachment?.fileUrl,
              finalVideoUrl: videoUrl,
            });

            // Create a cleaned embed URL candidate (this will handle query-only strings)
            const embedUrlCandidate = toYouTubeEmbedUrl(videoUrl);

            // Check if it's a valid video URL (file extension, YouTube embed, Vimeo, or Cloudflare Stream)
            const isValidVideoUrl =
              (videoUrl &&
                /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(videoUrl)) ||
              (embedUrlCandidate &&
                embedUrlCandidate.includes("youtube.com/embed")) ||
              (videoUrl &&
                /\b(vimeo\.com|cloudflarestream\.com)\b/i.test(videoUrl));

            // Check if it's a YouTube or Cloudflare Stream video
            const isYouTubeVideo =
              embedUrlCandidate?.includes("youtube.com/embed") ||
              /\b(youtube\.com|youtu\.be)\b/i.test(videoUrl) ||
              videoUrl.includes("youtu.be");
            const isCloudflareVideo = videoUrl.includes("cloudflarestream.com");
            console.log("videoUrl candidate:", videoUrl);
            console.log("isValidVideoUrl:", isValidVideoUrl);
            console.log("isYouTubeVideo:", isYouTubeVideo);
            console.log("isCloudflareVideo:", isCloudflareVideo);

            // Additional debugging for YouTube URL detection
            if (videoUrl) {
              console.log(
                "URL contains youtube.com:",
                videoUrl.includes("youtube.com")
              );
              console.log(
                "URL contains youtu.be:",
                videoUrl.includes("youtu.be")
              );
              console.log(
                "URL contains cloudflarestream.com:",
                videoUrl.includes("cloudflarestream.com")
              );
            }

            if (isValidVideoUrl) {
              console.log("Playing video with URL:", videoUrl);

              // Ensure we have a valid URL to work with
              if (!videoUrl || videoUrl.trim() === "") {
                console.error("No valid video URL found");
                return null;
              }

              if (isYouTubeVideo || isCloudflareVideo) {
                // Use iframe embed for YouTube and Cloudflare Stream videos (more reliable)
                let embedUrl = videoUrl;

                if (isYouTubeVideo) {
                  // Clean YouTube URL conversion - extract ID and rebuild without query params
                  console.log("Processing YouTube URL for embed:", videoUrl);
                  console.log("videoUrl type:", typeof videoUrl);
                  console.log("videoUrl length:", videoUrl?.length);
                  embedUrl = toYouTubeEmbedUrl(videoUrl);
                  console.log("Converted YouTube URL:", embedUrl);
                  console.log("embedUrl === videoUrl:", embedUrl === videoUrl);
                } else if (isCloudflareVideo) {
                  // Handle Cloudflare Stream
                  embedUrl = videoUrl.replace(
                    "/manifest/video.m3u8",
                    "/iframe"
                  );
                  console.log("Cloudflare URL:", videoUrl);
                  console.log("Converted Cloudflare URL:", embedUrl);
                }

                return (
                  <div className="w-full">
                    <div
                      className="relative"
                      style={{ paddingBottom: "56.25%", height: 0 }}
                    >
                      <iframe
                        src={embedUrl}
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={
                          isYouTubeVideo ? "YouTube Video" : "Video Player"
                        }
                      />
                    </div>
                  </div>
                );
              } else {
                // Use ReactPlayer for uploaded videos and other platforms
                return (
                  <div className="w-full">
                    <ReactPlayer
                      url={videoUrl}
                      controls={true}
                      width="100%"
                      height="400px"
                      onReady={() => console.log("Video player ready")}
                      onError={(error: any) =>
                        console.error("Video player error:", error)
                      }
                      onProgress={(progress: any) => {
                        const progressPercent = progress.played * 100;
                        onProgress?.(progressPercent);
                        if (progressPercent >= 95) {
                          onComplete?.();
                        }
                      }}
                      onEnded={() => onComplete?.()}
                      config={{
                        youtube: {
                          playerVars: {
                            modestbranding: 1,
                            rel: 0,
                            origin: window.location.origin,
                          },
                        },
                        file: {
                          attributes: {
                            controlsList: "nodownload",
                          },
                        },
                      }}
                    />
                  </div>
                );
              }
            }

            // No playable video URL found - show fallback UI with attachments if any
            return (
              <div className="h-64 border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Video className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    No video available
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    This lesson does not have a playable video URL.
                  </p>
                  {attachments.length > 0 ? (
                    <div className="space-y-2">
                      {attachments.map((att: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-white rounded shadow-sm"
                        >
                          <div className="flex items-center space-x-2">
                            <Video className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700">
                              {att.fileName || `Attachment ${idx + 1}`}
                            </span>
                          </div>
                          <a
                            href={att.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Open
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">
                        No attachments available. Contact the instructor for the
                        video resource.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        {content.type === "document" && (
          <div className="w-full">
            {(() => {
              console.log("Document content object:", content);
              console.log("Document content.content:", content.content);
              console.log("Document content.mediaUrl:", content.mediaUrl);
              console.log("Document content.attachments:", content.attachments);

              // For document types, ALWAYS prioritize file attachments over HTML content
              const documentUrl =
                content.mediaUrl ||
                content.downloadUrl ||
                (content.attachments && content.attachments.length > 0
                  ? content.attachments.find(
                      (att: any) => att.type === "file" && att.fileUrl
                    )?.fileUrl
                  : null);

              const documentAttachments =
                content.attachments?.filter(
                  (att: any) => att.type === "file"
                ) || [];

              console.log("Document URL found:", documentUrl);
              console.log(
                "Document attachments found:",
                documentAttachments.length
              );

              // If we have document files, render them with DocViewer
              if (documentUrl || documentAttachments.length > 0) {
                console.log("Rendering document files");
                return (
                  <div className="space-y-4">
                    {/* Main document display */}
                    {documentUrl && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <DocViewer
                          documents={[
                            { uri: documentUrl, fileName: content.title },
                          ]}
                          pluginRenderers={DocViewerRenderers}
                          style={{ height: 600 }}
                          config={{
                            header: {
                              disableHeader: false,
                              disableFileName: false,
                              retainURLParams: false,
                            },
                          }}
                        />
                      </div>
                    )}

                    {/* Additional attachments */}
                    {documentAttachments.length > 0 && (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">
                          Attachments
                        </h4>
                        <div className="space-y-2">
                          {documentAttachments.map(
                            (attachment: any, index: number) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded"
                              >
                                <div className="flex items-center space-x-2">
                                  <FileText className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">
                                    {attachment.fileName ||
                                      `Attachment ${index + 1}`}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {attachment.fileSize &&
                                      `${(attachment.fileSize / 1024).toFixed(
                                        1
                                      )} KB`}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      // Open document in preview modal or new tab
                                      window.open(attachment.fileUrl, "_blank");
                                    }}
                                    className="text-blue-600 hover:text-blue-700 text-sm px-2 py-1 rounded hover:bg-blue-50"
                                  >
                                    Preview
                                  </button>
                                  <a
                                    href={attachment.fileUrl}
                                    download
                                    className="text-gray-600 hover:text-gray-700 text-sm p-1 rounded hover:bg-gray-50"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Fallback to HTML content ONLY if no files found and content exists and has meaningful content
              const hasMeaningfulContent =
                content.content &&
                content.content.trim() &&
                content.content.trim() !== "<p></p>" &&
                content.content.trim() !== "<p><br></p>" &&
                content.content.trim() !== "<div></div>" &&
                content.content.trim() !== "<div><br></div>";

              if (hasMeaningfulContent) {
                console.log("Rendering HTML content");
                return (
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: content.content || "" }}
                  />
                );
              }

              // No content available
              console.log("No content available");
              return (
                <div className="text-gray-500 text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    No Document Content
                  </h4>
                  <p className="text-sm text-gray-600">
                    This document lesson has no attached files or content.
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {content.type === "image" && content.mediaUrl && (
          <img
            src={content.mediaUrl}
            alt={content.title}
            className="w-full h-auto rounded-lg"
          />
        )}

        {content.type === "link" && content.externalUrl && (
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600 mb-3">External Resource:</p>
            <a
              href={content.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <LinkIcon className="w-4 h-4" />
              <span>{content.externalUrl}</span>
            </a>
          </div>
        )}

        {content.type === "text" && content.content && (
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        )}
      </div>
    </div>
  );
};

const ModuleViewer: React.FC<ModuleViewerProps> = () => {
  const { courseId, moduleId } = useParams<{
    courseId: string;
    moduleId: string;
  }>();
  const navigate = useNavigate();

  const [module, setModule] = useState<Module | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [progress, setProgress] = useState<ModuleProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModuleData = async () => {
      if (!courseId || !moduleId) return;

      try {
        setLoading(true);

        // Fetch course data which includes modules with lessons for students
        const courseResponse = await eCourseService.getCourseById(courseId);
        const course = courseResponse.data;

        // Find the specific module
        const moduleData = course.modules?.find((m: any) => m._id === moduleId);
        if (!moduleData) {
          throw new Error("Module not found");
        }

        // For students, modules have 'lessons' instead of 'contents'
        const lessons = moduleData.lessons || [];

        // Transform lessons to match Content interface
        const transformedContents: Content[] = lessons.map((lesson: any) => {
          const attachments = lesson.attachments || [];
          const firstDocument = attachments.find(
            (att: any) => att.type === "file" && att.fileUrl
          );

          // Determine content type based on available data
          let contentType: Content["type"] = "text";
          if (lesson.videoUrl) {
            contentType = "video";
          } else if (lesson.fileUrl || firstDocument) {
            // Check if it's a document or image
            const fileUrl = lesson.fileUrl || firstDocument?.fileUrl || "";
            const ext = fileUrl.split(".").pop()?.toLowerCase();
            if (
              ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(
                ext || ""
              )
            ) {
              contentType = "document";
            } else if (
              ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext || "")
            ) {
              contentType = "image";
            } else if (
              ["mp4", "webm", "ogg", "mov", "m4v"].includes(ext || "")
            ) {
              contentType = "video";
            }
          } else if (lesson.content) {
            contentType = "text";
          }

          return {
            _id: lesson._id,
            title: lesson.title,
            description: lesson.description,
            type: contentType,
            content: lesson.content,
            mediaUrl:
              lesson.videoUrl || lesson.fileUrl || firstDocument?.fileUrl || "",
            externalUrl: lesson.externalUrl || "",
            downloadUrl: lesson.fileUrl || firstDocument?.fileUrl || "",
            attachments: attachments,
          };
        });

        // Mock progress data (TODO: implement real progress tracking)
        const mockProgress: ModuleProgress = {
          completionPercentage: 0,
          contentProgress: transformedContents.map((content) => ({
            contentId: content._id,
            completed: false,
            progressPercentage: 0,
          })),
        };

        setModule({
          _id: moduleData._id,
          title: moduleData.title,
          description: moduleData.description,
          estimatedDuration: moduleData.estimatedDuration,
          order: moduleData.order,
        });
        setContents(transformedContents);
        setProgress(mockProgress);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load module");
      } finally {
        setLoading(false);
      }
    };

    fetchModuleData();
  }, [courseId, moduleId]);

  const handleContentProgress = async (contentProgress: number) => {
    if (!moduleId || !contents[currentContentIndex]) return;

    try {
      // TODO: Replace with actual API call
      console.log(
        `Updating progress for content ${contents[currentContentIndex]._id}: ${contentProgress}%`
      );
    } catch (error) {
      console.error("Error updating content progress:", error);
    }
  };

  const handleContentComplete = async () => {
    if (!moduleId || !contents[currentContentIndex]) return;

    try {
      // Call the API to update progress
      const progressData = {
        status: "completed" as const,
        progress: 100,
        timeSpent: 0, // TODO: Track actual time spent
      };

      await eCourseService.updateContentProgress(
        courseId!,
        moduleId,
        contents[currentContentIndex]._id,
        progressData
      );

      // Move to next content if available
      if (currentContentIndex < contents.length - 1) {
        setCurrentContentIndex(currentContentIndex + 1);
      }
    } catch (error) {
      console.error("Error marking content as complete:", error);
    }
  };

  const goToPrevious = () => {
    if (currentContentIndex > 0) {
      setCurrentContentIndex(currentContentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentContentIndex < contents.length - 1) {
      setCurrentContentIndex(currentContentIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <BookOpen className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || "Module not found"}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const currentContent = contents[currentContentIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {module.title}
                </h1>
                <p className="text-sm text-gray-600">
                  {currentContentIndex + 1} of {contents.length} contents
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Progress: {progress?.completionPercentage || 0}%
              </div>

              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress?.completionPercentage || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Content Area */}
          <div className="lg:col-span-3">
            {currentContent ? (
              <ContentRenderer
                content={currentContent}
                onProgress={handleContentProgress}
                onComplete={handleContentComplete}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No content available
                </h3>
                <p className="text-gray-600">
                  This module doesn't have any content yet.
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={goToPrevious}
                disabled={currentContentIndex === 0}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>

              <div className="text-sm text-gray-600">
                {currentContentIndex + 1} of {contents.length}
              </div>

              <button
                onClick={goToNext}
                disabled={currentContentIndex === contents.length - 1}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Module Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                Module Details
              </h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {module.estimatedDuration} minutes
                  </span>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {contents.length} contents
                  </span>
                </div>
              </div>

              {module.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">{module.description}</p>
                </div>
              )}
            </div>

            {/* Content List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Contents</h3>
              </div>

              <div className="divide-y divide-gray-200">
                {contents.map((content, index) => (
                  <button
                    key={content._id}
                    onClick={() => setCurrentContentIndex(index)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                      index === currentContentIndex
                        ? "bg-blue-50 border-r-2 border-blue-600"
                        : ""
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {progress?.contentProgress?.find(
                          (cp: ContentProgress) => cp.contentId === content._id
                        )?.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {content.title}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {content.type}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleViewer;
